/**
 * Hook for initializing the active step - PERFORMANCE OPTIMIZED
 *
 * What it does:
 * - Waits for navigation steps to be loaded
 * - Sets the first step as active (only once)
 * - Marks the first step as visited
 * - Resets when navigation changes (like page refresh)
 *
 * Why we need this:
 * - When the app starts, we don't know which steps are available
 * - Once steps are loaded, we need to automatically select the first one
 * - We only want to do this once, not every time the component re-renders
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - Memoized callback prevents dispatch function recreation
 * - Combined effects reduce the number of effect executions
 * - Single focused effect handles both initialization and reset
 * - Minimal dependencies prevent unnecessary re-runs
 */

import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from '../../../services/serviceTypes';
import { addVisitedStep, setActiveStep } from '../data-store/copilotSlice';

const useStepInitializer = (leftNavLabels: string[]) => {
  const dispatch = useDispatch();
  const hasInitialized = useRef(false);

  const mode = useSelector((state: RootState) => state.copilot.ui.mode);

  // OPTIMIZATION: Memoized initialization callback
  // Prevents dispatch function recreation on every render
  // Only recreates when dispatch reference changes (rarely)
  // Reduces effect dependency complexity
  const initializeFirstStep = useCallback(
    (firstStep: string) => {
      // In edit mode, don't set any step as active - let user choose
      if (mode === 'edit') {
        // don't set any active step
      } else {
        dispatch(setActiveStep(firstStep));
        dispatch(addVisitedStep(firstStep));
      }

      hasInitialized.current = true;
    },
    [dispatch, mode]
  );

  // OPTIMIZATION: Combined effect for initialization and reset
  // BEFORE: Two separate useEffect hooks for initialization and reset
  // AFTER: Single focused effect that handles both scenarios
  // BENEFIT: Fewer effect executions, cleaner logic flow
  useEffect(() => {
    const hasLabels = leftNavLabels.length > 0;

    if (hasLabels && !hasInitialized.current) {
      // Initialize first step when labels are available
      const firstStep = leftNavLabels[0];

      if (firstStep) {
        initializeFirstStep(firstStep);
      }
    } else if (!hasLabels) {
      // Reset initialization flag when navigation is cleared
      // (happens on page refresh or when switching contexts)
      hasInitialized.current = false;
    }
  }, [leftNavLabels, initializeFirstStep]);
};

export default useStepInitializer;
